<template>
    <v-dialog :model-value="modelValue" max-width="1000" scrollable
              @update:modelValue="$emit('update:modelValue', $event)">
        <v-card v-if="reseller">
            <v-card-title class="d-flex align-center">
                <span>{{ reseller.name }}</span>
                <v-chip size="small" variant="tonal" class="ml-3">{{ reseller.code }}</v-chip>
                <v-spacer/>
                <v-btn icon="mdi-close" variant="text" @click="$emit('update:modelValue', false)"/>
            </v-card-title>

            <v-card-text>
                <v-alert v-for="(e, i) in errors" :key="i" type="error" variant="tonal" class="mb-3">{{ e }}</v-alert>

                <v-row class="mb-2">
                    <v-col cols="12" sm="4">
                        <v-text-field v-model="from" label="From" type="date" density="compact" hide-details
                                      @update:modelValue="load"/>
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field v-model="to" label="To" type="date" density="compact" hide-details
                                      @update:modelValue="load"/>
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-select v-model="status" :items="statuses" label="Status" density="compact"
                                  hide-details clearable @update:modelValue="load"/>
                    </v-col>
                </v-row>

                <!--
                  Grouped by currency and never summed across it. Plans are
                  multi-currency, so one combined figure would be meaningless and a
                  payout is settled per currency.
                -->
                <div class="text-subtitle-2 mb-1">Totals by currency</div>
                <v-table density="compact" class="mb-4">
                    <thead>
                    <tr>
                        <th>Currency</th>
                        <th>Status</th>
                        <th class="text-end">Invoices</th>
                        <th class="text-end">Invoiced</th>
                        <th class="text-end">Commission</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(t, i) in totals" :key="i">
                        <td>{{ t.currency || '-' }}</td>
                        <td>{{ t.status }}</td>
                        <td class="text-end">{{ t.invoices }}</td>
                        <td class="text-end">{{ money(t.base, t.currency) }}</td>
                        <td class="text-end font-weight-medium">{{ money(t.commission, t.currency) }}</td>
                    </tr>
                    <tr v-if="!totals.length">
                        <td colspan="5" class="text-center text-medium-emphasis">
                            Nothing earned in this period yet.
                        </td>
                    </tr>
                    </tbody>
                </v-table>

                <div class="text-subtitle-2 mb-1">Commission per invoice</div>
                <v-data-table
                    :headers="headers"
                    :items="rows"
                    :loading="loading"
                    density="compact"
                    items-per-page="25">
                    <template #item.accrued_at="{ item }">{{ formatDate(item.accrued_at) }}</template>
                    <template #item.company="{ item }">{{ item.company?.company_name ?? '-' }}</template>
                    <template #item.base_amount="{ item }">{{ money(item.base_amount, item.currency) }}</template>
                    <template #item.rate_percent="{ item }">{{ parseFloat(item.rate_percent) }}%</template>
                    <template #item.commission_amount="{ item }">
                        {{ money(item.commission_amount, item.currency) }}
                    </template>
                    <template #item.kind="{ item }">
                        <v-chip size="x-small" variant="tonal">{{ item.kind }}</v-chip>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, watch} from 'vue';
import {send} from '@/composables/apiClient';

const props = defineProps({
    modelValue: {type: Boolean, default: false},
    reseller: {type: Object, default: null},
});
defineEmits(['update:modelValue']);

const statuses = [
    {title: 'Accrued', value: 'accrued'},
    {title: 'Paid', value: 'paid'},
    {title: 'Reversed', value: 'reversed'},
];

const headers = [
    {title: 'Date', key: 'accrued_at'},
    {title: 'Company', key: 'company'},
    {title: 'Type', key: 'kind'},
    {title: 'Invoiced', key: 'base_amount', align: 'end'},
    {title: 'Rate', key: 'rate_percent', align: 'end'},
    {title: 'Commission', key: 'commission_amount', align: 'end'},
    {title: 'Status', key: 'status'},
];

const totals = ref([]);
const rows = ref([]);
const loading = ref(false);
const errors = ref([]);
const from = ref('');
const to = ref('');
const status = ref(null);

/**
 * Currency-aware, because the platform bills in more than one and a bare $ has
 * been a recurring bug here. Falls back to the code when Intl does not know it.
 */
const money = (value, currency) => {
    const amount = parseFloat(value ?? 0);
    if (!currency) return amount.toFixed(2);
    try {
        return new Intl.NumberFormat(undefined, {style: 'currency', currency}).format(amount);
    } catch {
        return `${amount.toFixed(2)} ${currency}`;
    }
};

const formatDate = (v) => (v ? new Date(v).toLocaleDateString() : '-');

async function load() {
    if (!props.reseller?.id) return;
    loading.value = true;
    errors.value = [];
    try {
        const response = await send('get', `admin/resellers/${props.reseller.id}/commissions`, {
            from: from.value || undefined,
            to: to.value || undefined,
            status: status.value || undefined,
            rows: 200,
        });
        const data = response.data?.data ?? {};
        totals.value = data.totals_by_currency ?? [];
        rows.value = data.commissions?.data ?? [];
    } catch (error) {
        errors.value.push(error.response?.data?.message || 'Could not load commissions.');
    } finally {
        loading.value = false;
    }
}

watch(() => props.modelValue, (open) => {
    if (!open) return;
    // Reset the window on each open so a filter from the previous partner does not
    // silently hide this one's earnings.
    from.value = '';
    to.value = '';
    status.value = null;
    load();
});
</script>
